import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { ArrowRight, Truck, Star, ShoppingBag, Clock, Shield, ChevronLeft, ChevronRight, Tag, Percent } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Hero Image Carousel Component
const HeroImageCarousel = () => {
  const [currentImage, setCurrentImage] = useState(0);
  const images = [
    { url: "https://lh3.googleusercontent.com/p/AF1QipOPffdkBhPK7ChlXcStxIUmRPfZjpFq3CWg-yeS=s1360-w1360-h1020-rw", alt: "Juncia Caribbean Market Store" }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % images.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [images.length]);

  return (
    <div className="relative">
      <AnimatePresence mode="wait">
        <motion.img
          key={currentImage}
          src={images[currentImage].url}
          alt={images[currentImage].alt}
          className="rounded-3xl shadow-2xl w-full h-[400px] object-cover"
          initial={{ opacity: 0, scale: 1.05 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.5 }}
        />
      </AnimatePresence>
      <div className="absolute -bottom-4 -left-4 bg-card rounded-2xl p-4 shadow-xl border border-border/50">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
            <Truck className="w-6 h-6 text-primary" />
          </div>
          <div>
            <p className="font-semibold">$5 Flat Delivery</p>
            <p className="text-sm text-muted-foreground">Anywhere in Savannah</p>
          </div>
        </div>
      </div>
      {/* Image dots */}
      <div className="absolute bottom-4 right-4 flex gap-2">
        {images.map((_, idx) => (
          <button
            key={idx}
            onClick={() => setCurrentImage(idx)}
            className={`w-2.5 h-2.5 rounded-full transition-colors ${
              idx === currentImage ? 'bg-white' : 'bg-white/40'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

// Promotional Banner Carousel Component
const PromoBanner = ({ promotions }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (promotions.length <= 1) return;
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % promotions.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [promotions.length]);

  if (!promotions || promotions.length === 0) return null;

  const nextSlide = () => setCurrentIndex((prev) => (prev + 1) % promotions.length);
  const prevSlide = () => setCurrentIndex((prev) => (prev - 1 + promotions.length) % promotions.length);

  return (
    <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-secondary/90 to-primary/90" data-testid="promo-banner">
      <AnimatePresence mode="wait">
        <motion.div
          key={currentIndex}
          initial={{ opacity: 0, x: 100 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -100 }}
          transition={{ duration: 0.5 }}
          className="relative"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-6 lg:p-10">
            <div className="flex flex-col justify-center text-white">
              <div className="flex items-center gap-2 mb-3">
                {promotions[currentIndex].discount_percent && (
                  <Badge className="bg-accent text-accent-foreground">
                    <Percent className="w-3 h-3 mr-1" />
                    {promotions[currentIndex].discount_percent}% OFF
                  </Badge>
                )}
                {promotions[currentIndex].promo_code && (
                  <Badge variant="outline" className="border-white/50 text-white">
                    <Tag className="w-3 h-3 mr-1" />
                    {promotions[currentIndex].promo_code}
                  </Badge>
                )}
              </div>
              <h2 className="font-heading text-2xl lg:text-4xl font-bold mb-3">
                {promotions[currentIndex].title}
              </h2>
              <p className="text-white/90 text-base lg:text-lg mb-6">
                {promotions[currentIndex].description}
              </p>
              <Button 
                size="lg"
                variant="secondary"
                className="rounded-full w-fit"
                onClick={() => navigate('/products')}
                data-testid="promo-shop-btn"
              >
                Shop Now
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
            <div className="hidden lg:block">
              <img
                src={promotions[currentIndex].image_url}
                alt={promotions[currentIndex].title}
                className="w-full h-64 object-cover rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </motion.div>
      </AnimatePresence>

      {promotions.length > 1 && (
        <>
          <button
            onClick={prevSlide}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center text-white transition-colors"
            data-testid="promo-prev"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center text-white transition-colors"
            data-testid="promo-next"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
            {promotions.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentIndex(idx)}
                className={`w-2 h-2 rounded-full transition-colors ${
                  idx === currentIndex ? 'bg-white' : 'bg-white/40'
                }`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const ProductCard = ({ product, onAddToCart }) => {
  const navigate = useNavigate();
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -5 }}
      transition={{ duration: 0.3 }}
    >
      <Card 
        className="group cursor-pointer overflow-hidden rounded-2xl bg-card border border-border/50 hover:border-primary/50 transition-all duration-300 hover:shadow-xl"
        onClick={() => navigate(`/products/${product.id}`)}
        data-testid={`product-card-${product.id}`}
      >
        <div className="relative aspect-square overflow-hidden">
          <img
            src={product.image_url}
            alt={product.name}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          />
          {product.is_featured && (
            <Badge className="absolute top-3 left-3 bg-accent text-accent-foreground">
              Featured
            </Badge>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h3 className="font-heading font-semibold text-lg line-clamp-1">{product.name}</h3>
              <p className="text-muted-foreground text-sm mt-1 line-clamp-2">{product.description}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 mt-2">
            <div className="flex items-center gap-1">
              <Star className="w-4 h-4 fill-accent text-accent" />
              <span className="text-sm font-medium">{product.rating}</span>
            </div>
            <span className="text-muted-foreground text-sm">({product.review_count})</span>
          </div>
          <div className="flex items-center justify-between mt-4">
            <div>
              <span className="font-mono text-xl font-bold text-primary">${product.price.toFixed(2)}</span>
              <span className="text-muted-foreground text-sm ml-1">/{product.unit}</span>
            </div>
            <Button
              size="sm"
              className="rounded-full"
              onClick={(e) => {
                e.stopPropagation();
                onAddToCart(product.id);
              }}
              data-testid={`add-to-cart-${product.id}`}
            >
              <ShoppingBag className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

const CategoryCard = ({ category }) => {
  const navigate = useNavigate();
  
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      whileHover={{ scale: 1.02 }}
      transition={{ duration: 0.3 }}
    >
      <Card 
        className="relative cursor-pointer overflow-hidden rounded-2xl h-48 group"
        onClick={() => navigate(`/products?category=${category.id}`)}
        data-testid={`category-card-${category.id}`}
      >
        <img
          src={category.image}
          alt={category.name}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
        />
        <div className="absolute inset-0 category-overlay" />
        <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
          <h3 className="font-heading font-bold text-lg">{category.name}</h3>
          <p className="text-sm text-white/80">{category.description}</p>
        </div>
      </Card>
    </motion.div>
  );
};

export default function HomePage() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { addToCart } = useCart();
  const [allProducts, setAllProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Seed data first
      await axios.post(`${API}/seed`).catch(() => {});
      
      const [productsRes, categoriesRes, promosRes] = await Promise.all([
        axios.get(`${API}/products`),
        axios.get(`${API}/categories`),
        axios.get(`${API}/promotions`)
      ]);
      setAllProducts(productsRes.data);
      setCategories(categoriesRes.data);
      setPromotions(promosRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async (productId) => {
    if (!token) {
      toast.error('Please login to add items to cart');
      navigate('/login');
      return;
    }
    try {
      await addToCart(productId);
      toast.success('Added to cart!');
    } catch (error) {
      toast.error('Failed to add to cart');
    }
  };

  return (
    <div className="min-h-screen">
      {/* Caribbean Flags Banner */}
      <div className="bg-gradient-to-r from-primary/20 via-secondary/20 to-accent/20 py-4 overflow-hidden">
        <div className="flex items-center justify-center gap-6 text-5xl">
          <span title="Jamaica">🇯🇲</span>
          <span title="Haiti">🇭🇹</span>
          <span title="Trinidad & Tobago">🇹🇹</span>
          <span title="Barbados">🇧🇧</span>
          <span title="Bahamas">🇧🇸</span>
          <span title="Dominican Republic">🇩🇴</span>
          <span title="Cuba">🇨🇺</span>
          <span title="Puerto Rico">🇵🇷</span>
          <span title="Guadeloupe">🇬🇵</span>
          <span title="St. Lucia">🇱🇨</span>
          <span title="Grenada">🇬🇩</span>
          <span title="St. Vincent">🇻🇨</span>
          <span title="Antigua">🇦🇬</span>
          <span title="Dominica">🇩🇲</span>
        </div>
      </div>

      {/* Hero Section */}
      <section className="hero-pattern relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <motion.div 
              className="lg:col-span-6"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Badge className="bg-accent/20 text-accent-foreground mb-4 delivery-badge">
                <Truck className="w-4 h-4 mr-2" />
                $5 Flat Delivery on All Orders
              </Badge>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tight mb-6">
                <span className="block text-5xl sm:text-6xl lg:text-8xl text-primary mb-2">Juncia</span>
                <span className="block">Caribbean Market</span>
              </h1>
              <p className="text-lg text-muted-foreground mb-8 max-w-lg">
                Authentic Caribbean groceries, fresh produce, and specialty spices from Jamaica, Haiti, Trinidad & beyond. 
                Delivered to your door in Savannah for just $5!
              </p>
              
              {/* Flags under description */}
              <div className="flex flex-wrap gap-3 mb-6 text-4xl">
                <span>🇯🇲</span><span>🇭🇹</span><span>🇹🇹</span><span>🇧🇧</span><span>🇩🇴</span><span>🇨🇺</span><span>🇵🇷</span><span>🇬🇩</span>
              </div>
              <div className="flex flex-wrap gap-4">
                <Button 
                  size="lg" 
                  className="rounded-full px-8"
                  onClick={() => navigate('/products')}
                  data-testid="shop-now-btn"
                >
                  Shop Now
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button 
                  variant="outline" 
                  size="lg" 
                  className="rounded-full px-8"
                  onClick={() => navigate('/categories')}
                  data-testid="browse-categories-btn"
                >
                  Browse Categories
                </Button>
              </div>
            </motion.div>
            <motion.div 
              className="lg:col-span-6"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <HeroImageCarousel />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Promotional Banners */}
      {promotions.length > 0 && (
        <section className="py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <PromoBanner promotions={promotions} />
          </div>
        </section>
      )}

      {/* Features */}
      <section className="py-12 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: Truck, title: '$5 Flat Delivery', desc: 'On every order, anywhere in Savannah' },
              { icon: Clock, title: 'Same Day Delivery', desc: 'Order before 2pm' },
              { icon: Shield, title: 'Fresh Guarantee', desc: 'Quality assured' },
            ].map((feature, i) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className="flex items-center gap-4 p-6 rounded-2xl bg-card border border-border/50"
              >
                <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                  <feature.icon className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-heading font-semibold">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground">{feature.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="font-heading text-3xl font-bold">Shop by Category</h2>
              <p className="text-muted-foreground mt-1">Find exactly what you're looking for</p>
            </div>
            <Button 
              variant="ghost" 
              onClick={() => navigate('/categories')}
              data-testid="view-all-categories-btn"
            >
              View All
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {[...Array(6)].map((_, i) => (
                <Skeleton key={i} className="h-48 rounded-2xl" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {categories.map((category, i) => (
                <motion.div key={category.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.1 }}>
                  <CategoryCard category={category} />
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* All Products */}
      <section className="py-16 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="font-heading text-3xl font-bold">Our Products</h2>
            <p className="text-muted-foreground mt-1">Fresh Caribbean groceries & specialty items</p>
          </div>
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[...Array(8)].map((_, i) => (
                <Skeleton key={i} className="h-80 rounded-2xl" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {allProducts.map((product) => (
                <ProductCard key={product.id} product={product} onAddToCart={handleAddToCart} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-3xl bg-primary p-8 md:p-16">
            <div className="relative z-10">
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary-foreground mb-4">
                Ready to taste the Caribbean?
              </h2>
              <div className="flex gap-3 text-4xl mb-4">
                🇯🇲 🇭🇹 🇹🇹 🇧🇧 🇩🇴 🇨🇺 🇵🇷 🇬🇩 🇱🇨 🇻🇨
              </div>
              <p className="text-primary-foreground/80 text-lg mb-8 max-w-lg">
                Join thousands of happy customers enjoying authentic Caribbean flavors delivered fresh to their homes in Savannah.
              </p>
              <Button 
                size="lg" 
                variant="secondary"
                className="rounded-full px-8"
                onClick={() => navigate(user ? '/products' : '/register')}
                data-testid="cta-btn"
              >
                {user ? 'Start Shopping' : 'Create Account'}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
            <div className="absolute right-0 top-0 bottom-0 w-1/2 opacity-20">
              <img
                src="https://images.unsplash.com/photo-1768734836337-fa47d15984b3?w=800"
                alt=""
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
